import type { FileKind } from '../types';

/**
 * Persistencia del job de procesamiento en vuelo, para sobrevivir un reload.
 *
 * El backend persiste el resultado de `processMenuV4` bajo el `jobId` que el
 * cliente envía (ver `menuJobRecovery.ts`). Este módulo guarda en localStorage
 * la referencia mínima para retomar tras recargar la página: el jobId, cuándo
 * arrancó, y los metadatos de los archivos ya subidos (nombre, tipo, tamaño y
 * la download URL de Storage — que sirve como preview sin el `File` local).
 *
 * Mismo patrón defensivo que `cooldown.ts`: localStorage puede no existir
 * (SSR, privacidad) y el contenido puede estar corrupto — nunca lanzar.
 */

export interface PendingJobFile {
  name: string;
  kind: FileKind;
  sizeMB: number;
  /** Download URL de Firebase Storage; usable como `src` de preview tras reload. */
  url: string;
}

export interface PendingJob {
  jobId: string;
  /** Date.now() del arranque del batch; alimenta el timer y el TTL. */
  startedAt: number;
  files: PendingJobFile[];
}

export const PENDING_JOB_KEY = 'plick_smart_menu_pending_job';

/**
 * Más allá de esta edad un job no vale la pena recuperarse: el procesamiento
 * dura ≤15 min y el resultado persistido vive bajo el TTL de 7 días del bucket,
 * pero un usuario que vuelve días después espera empezar de cero, no un modal
 * de un intento que ya ni recuerda.
 */
export const PENDING_JOB_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const safeStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const isPendingJob = (val: unknown): val is PendingJob => {
  if (typeof val !== 'object' || val === null) return false;
  const job = val as Partial<PendingJob>;
  return (
    typeof job.jobId === 'string' &&
    job.jobId.length > 0 &&
    typeof job.startedAt === 'number' &&
    Array.isArray(job.files) &&
    job.files.every(
      (f) =>
        typeof f === 'object' &&
        f !== null &&
        typeof (f as PendingJobFile).name === 'string' &&
        typeof (f as PendingJobFile).url === 'string',
    )
  );
};

/**
 * Guarda la referencia del job en vuelo. Silencioso ante storage no disponible.
 *
 * @param {PendingJob} job Referencia mínima del batch (jobId + archivos subidos).
 */
export const savePendingJob = (job: PendingJob): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(PENDING_JOB_KEY, JSON.stringify(job));
  } catch {
    /* cuota llena o storage bloqueado: el flujo sigue sin persistencia */
  }
};

/**
 * Carga el job pendiente si existe, es válido y no excede el TTL.
 * Registros stale o corruptos se eliminan en el acto.
 *
 * @returns {PendingJob | null} El job recuperable, o null si no hay nada que retomar.
 */
export const loadPendingJob = (): PendingJob | null => {
  const storage = safeStorage();
  if (!storage) return null;
  const raw = storage.getItem(PENDING_JOB_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPendingJob(parsed)) {
      storage.removeItem(PENDING_JOB_KEY);
      return null;
    }
    if (Date.now() - parsed.startedAt > PENDING_JOB_MAX_AGE_MS) {
      storage.removeItem(PENDING_JOB_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(PENDING_JOB_KEY);
    return null;
  }
};

/** Elimina la referencia del job (al completar, fallar en firme o reiniciar). */
export const clearPendingJob = (): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(PENDING_JOB_KEY);
  } catch {
    /* no-op */
  }
};
